import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fetchSiteConfig, getCachedSiteConfig } from '../utils/siteConfigApi';
import { PLATFORM_NAME } from '../constants/brand';

/** Keep JobScribe casing consistent even if admin settings use a different case. */
function normalizePlatformName(name) {
  const raw = String(name || '').trim();
  if (!raw) return PLATFORM_NAME;
  if (raw.replace(/\s+/g, '').toLowerCase() === 'jobscribe') return PLATFORM_NAME;
  return raw;
}

const SiteConfigContext = createContext({
  loading: true,
  config: null,
  signupAllowed: true,
  signupBlockMessage: null,
  platformName: PLATFORM_NAME,
  googleLoginEnabled: false,
  refresh: () => {},
});

export function SiteConfigProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(() => getCachedSiteConfig());

  const load = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const next = await fetchSiteConfig({ forceRefresh });
      setConfig(next);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(false);
  }, []);

  const value = useMemo(
    () => ({
      loading,
      config,
      signupAllowed: config?.signup_allowed !== false,
      signupBlockMessage: config?.signup_block_message || null,
      platformName: normalizePlatformName(config?.platform_name),
      googleLoginEnabled: config?.google_login_enabled === true,
      refresh: () => load(true),
    }),
    [loading, config]
  );

  return (
    <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
